import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Card, HStack, VStack } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';
import { Spinner, Alert } from '@components/base/feedback';
import { TextField, Checkbox, Select } from '@components/base/inputs';
import { erpEndpointsService } from '../../api/erpEndpointsService';
import { erpRegistriesService } from '../../api/erpRegistriesService';
import { tenantsService } from '../../api/tenantsService';
import { useAdminAuthStore } from '../../auth/store';
import type { DryRunResponse } from '../../api/erpEndpointsService';

// ---------------------------------------------------------------------------
// HTTP method badge colors
// ---------------------------------------------------------------------------

const METHOD_BADGE_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'error' | 'primary' | 'default'> = {
  GET: 'success',
  POST: 'info',
  PUT: 'warning',
  PATCH: 'warning',
  DELETE: 'error',
};

// ---------------------------------------------------------------------------
// JSON validation helper
// ---------------------------------------------------------------------------

function parseJsonString(value: string): { valid: boolean; data: Record<string, unknown> | null } {
  if (value.trim() === '') {
    return { valid: true, data: null };
  }
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return { valid: true, data: parsed as Record<string, unknown> };
    }
    return { valid: false, data: null };
  } catch {
    return { valid: false, data: null };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EndpointDryRunPage() {
  const { registrySlug, endpointSlug } = useParams<{
    registrySlug: string;
    endpointSlug: string;
  }>();

  const activeTenantId = useAdminAuthStore((s) => s.activeTenantId);
  const spoofedTenantId = useAdminAuthStore((s) => s.spoofedTenantId);
  const spoofedBearerToken = useAdminAuthStore((s) => s.spoofedBearerToken);
  const spoofedSessionId = useAdminAuthStore((s) => s.spoofedSessionId);
  const [selectedTenantId, setSelectedTenantId] = useState(spoofedTenantId ?? activeTenantId ?? '');
  const [dataInput, setDataInput] = useState('');
  const [headersInput, setHeadersInput] = useState('');
  const [dryRunFlag, setDryRunFlag] = useState(true);
  const [result, setResult] = useState<DryRunResponse | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    data?: string;
    headers?: string;
  }>({});

  /* v8 ignore start */
  const registryHref = `/admin/erp/registries/${registrySlug ?? ''}`;
  /* v8 ignore stop */

  // ---- Fetch registry name for breadcrumb ----------------------------------

  const { data: registryResponse } = useQuery({
    queryKey: ['admin', 'erp', 'registries', registrySlug],
    queryFn: () => {
      /* v8 ignore start */
      if (!registrySlug) return Promise.reject(new Error('Registry slug is required'));
      /* v8 ignore stop */
      return erpRegistriesService.get(registrySlug);
    },
    enabled: !!registrySlug,
  });

  const registry = registryResponse?.data;
  const registryName = registry?.name ?? 'Registry';

  // ---- Fetch tenant list for selector ---------------------------------------

  const { data: tenantsResponse } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => tenantsService.list(),
  });

  const tenants = tenantsResponse?.data ?? [];

  const tenantOptions = tenants.map((t) => ({
    value: String(t.id),
    label: t.name,
  }));

  useEffect(() => {
    const firstTenant = tenants[0];
    if (firstTenant && !selectedTenantId) {
      const fallback = spoofedTenantId ?? activeTenantId ?? String(firstTenant.id);
      setSelectedTenantId(fallback);
    }
  }, [tenants, selectedTenantId, spoofedTenantId, activeTenantId]);

  // ---- Pre-fill request data for login_bearer registries --------------------

  useEffect(() => {
    if (registry?.auth_type === 'login_bearer' && dataInput === '') {
      setDataInput(JSON.stringify({ uid: 'SYSTEM', passwd: 'TZTUFN', language: 'fr' }, null, 2));
    }
  }, [registry?.auth_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Fetch endpoint info -------------------------------------------------

  const {
    data: endpointResponse,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['admin', 'erp', 'registries', registrySlug, 'endpoints', endpointSlug],
    queryFn: () => {
      /* v8 ignore start */
      if (!registrySlug || !endpointSlug) return Promise.reject(new Error('Registry slug and Endpoint slug are required'));
      /* v8 ignore stop */
      return erpEndpointsService.get(registrySlug, endpointSlug);
    },
    enabled: !!registrySlug && !!endpointSlug,
  });

  const endpoint = endpointResponse?.data;

  // ---- Dry run mutation ----------------------------------------------------

  const dryRunMutation = useMutation({
    mutationFn: () => {
      /* v8 ignore start */
      if (!registrySlug || !endpointSlug) return Promise.reject(new Error('Registry slug and Endpoint slug are required'));
      /* v8 ignore stop */

      const parsedData = parseJsonString(dataInput);
      const parsedHeaders = parseJsonString(headersInput);

      const errors: { data?: string; headers?: string } = {};
      if (!parsedData.valid) {
        errors.data = 'Invalid JSON format';
      }
      if (!parsedHeaders.valid) {
        errors.headers = 'Invalid JSON format';
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return Promise.reject(new Error('Please fix validation errors before executing.'));
      }

      setValidationErrors({});

      return erpEndpointsService.dryRun(
        registrySlug,
        endpointSlug,
        {
          data: parsedData.data ?? undefined,
          headers: parsedHeaders.data as Record<string, string> | undefined,
          dry_run: dryRunFlag,
        },
        selectedTenantId || undefined,
        spoofedBearerToken,
        spoofedSessionId,
      );
    },
    onSuccess: (data) => {
      setResult(data);
    },
  });

  // ---- Execute handler -----------------------------------------------------

  const handleExecute = useCallback(() => {
    dryRunMutation.mutate();
  }, [dryRunMutation]);

  // ---- Render: loading state -----------------------------------------------

  if (isLoading) {
    return (
      <VStack gap={6} align="center" className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading endpoint...</Text>
      </VStack>
    );
  }

  // ---- Render: error state -------------------------------------------------

  if (fetchError || !endpoint) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/erp/registries">ERP Registries</Breadcrumb.Item>
          <Breadcrumb.Item href={registryHref}>
            {registryName}
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Dry Run</Breadcrumb.Item>
        </Breadcrumb>
        <Alert variant="error" title="Failed to load endpoint">
          {fetchError instanceof Error
            ? fetchError.message
            : 'Endpoint not found or an unexpected error occurred.'}
        </Alert>
      </VStack>
    );
  }

  // ---- Render: main --------------------------------------------------------

  return (
    <VStack gap={6}>
      {/* Breadcrumb navigation */}
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/erp/registries">ERP Registries</Breadcrumb.Item>
        <Breadcrumb.Item href={registryHref}>
          {registryName}
        </Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>
          {endpoint.name} - Dry Run
        </Breadcrumb.Item>
      </Breadcrumb>

      {/* Page header */}
      <VStack gap={2}>
        <Text as="h1" size="2xl" weight="bold">
          Dry Run: {endpoint.name}
        </Text>
        <HStack gap={3} align="center">
          <Badge
            variant={METHOD_BADGE_VARIANT[endpoint.http_method] ?? 'default'}
            size="sm"
          >
            {endpoint.http_method}
          </Badge>
          <Text size="sm" color="muted">{endpoint.path}</Text>
        </HStack>
      </VStack>

      {/* Request form */}
      <Card>
        <Card.Header title="Request" />
        <Card.Content>
          <VStack gap={4}>
            <Select
              label="Tenant"
              options={tenantOptions}
              value={selectedTenantId}
              onChange={(value) => { setSelectedTenantId(value as string); }}
            />

            <TextField
              label="Request Data (JSON)"
              placeholder='{"key": "value"}'
              value={dataInput}
              onChange={(e) => { setDataInput(e.target.value); }}
              error={!!validationErrors.data}
              errorMessage={validationErrors.data}
            />

            <TextField
              label="Additional Headers (JSON)"
              placeholder='{"Authorization": "Bearer token"}'
              value={headersInput}
              onChange={(e) => { setHeadersInput(e.target.value); }}
              error={!!validationErrors.headers}
              errorMessage={validationErrors.headers}
            />

            <Checkbox
              label="Dry run mode (simulated, no actual HTTP call)"
              checked={dryRunFlag}
              onChange={(e) => { setDryRunFlag(e.target.checked); }}
            />

            <HStack>
              <Button
                variant="primary"
                leftIcon={<Icon icon={Play} size="sm" />}
                loading={dryRunMutation.isPending}
                onClick={handleExecute}
              >
                Execute
              </Button>
            </HStack>

            {dryRunMutation.isError && (
              <Alert variant="error" title="Execution failed">
                {dryRunMutation.error instanceof Error
                  ? dryRunMutation.error.message
                  : 'An unexpected error occurred.'}
              </Alert>
            )}
          </VStack>
        </Card.Content>
      </Card>

      {/* Results */}
      {result && (
        <Card>
          <Card.Header
            title="Response"
            action={
              <Badge variant={result.success ? 'success' : 'error'} size="sm">
                {result.success ? 'Success' : 'Error'}
              </Badge>
            }
          />
          <Card.Content>
            <VStack gap={4}>
              {/* Response data */}
              {result.data !== null && result.data !== undefined && (
                <VStack gap={1}>
                  <Text size="sm" weight="medium" color="muted">
                    Data
                  </Text>
                  <pre className="p-4 rounded-md overflow-auto text-sm font-mono max-h-96">
                    {typeof result.data === 'string'
                      ? result.data
                      : JSON.stringify(result.data, null, 2)}
                  </pre>
                </VStack>
              )}

              {/* Error details */}
              {result.error !== null && result.error !== undefined && (
                <VStack gap={1}>
                  <Text size="sm" weight="medium" color="error">
                    Error
                  </Text>
                  <pre className="p-4 rounded-md overflow-auto text-sm font-mono max-h-96">
                    {typeof result.error === 'string'
                      ? result.error
                      : JSON.stringify(result.error, null, 2)}
                  </pre>
                </VStack>
              )}

              {/* Metadata */}
              {result.metadata && Object.keys(result.metadata).length > 0 && (
                <VStack gap={1}>
                  <Text size="sm" weight="medium" color="muted">
                    Metadata
                  </Text>
                  <pre className="p-4 rounded-md overflow-auto text-sm font-mono max-h-96">
                    {JSON.stringify(result.metadata, null, 2)}
                  </pre>
                </VStack>
              )}
            </VStack>
          </Card.Content>
        </Card>
      )}
    </VStack>
  );
}
